import React, { useEffect, useState } from "react";
import { Typography, Card } from "@mui/material";
import { getTokenizationKeyCustomer } from "../../../../plugins/ApiHandler";
import { useUserType } from "../../../../contexts/UserTypeContext";
import { cleanupPaymentMethods, loadApplePayScript } from "../../../../plugins/helpers";

const ApplePayPayment = ({ paymentFormik, CustomerId }) => {
  const [publicKey, setPublicKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [buttonNotFound, setButtonNotFound] = useState(false);
  const [error, setError] = useState("");
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";

  useEffect(() => {
    const loadApplePay = async () => {
      try {
        cleanupPaymentMethods();

        const response = await getTokenizationKeyCustomer(CustomerId);
        
        if (response?.PublicKey && response.PublicKey.trim() !== "") {
          setPublicKey(response.PublicKey);
          await loadApplePayScript(response.PublicKey, paymentFormik, isDonationMode);
          
          setTimeout(() => {
            const applePayButton = document.querySelector("#applepaybutton");
            const applePayElements = document.querySelectorAll("[data-apple-pay]");
            const applePayButtonContent = applePayButton?.innerHTML || "";
            const hasApplePayContent =
              applePayButtonContent.includes("Apple Pay") ||
              applePayButtonContent.includes("apple-pay") ||
              applePayElements.length > 0;

            if (
              !applePayButton ||
              (!hasApplePayContent && applePayElements.length === 0)
            ) {
              setButtonNotFound(true);
            } else {
              setButtonNotFound(false);
            }
          }, 3000);

          setLoading(false);
          setError(""); // Clear any previous errors
        } else {
          setLoading(false);
          setError("No public key available for Apple Pay");
        }
      } catch (error) {
        setLoading(false);
        setError(error?.message || "Failed to load Apple Pay");
      }
    };

    const isHTTPS = window.location.protocol === "https:";
    const isAndroid = /Android/.test(navigator.userAgent);
    const hasCustomerId = CustomerId && CustomerId.trim() !== "";
    const hasAmount =
      paymentFormik?.values?.amount &&
      parseFloat(paymentFormik.values.amount) > 0;

    if (!isHTTPS) {
      setLoading(false);
    } else if (isAndroid) {
      setLoading(false);
    } else if (!hasCustomerId) {
      setLoading(false);
    } else if (!hasAmount) {
      setLoading(false);
    } else {
      loadApplePay();
    }

    return () => {
      cleanupPaymentMethods();
    };
  }, [CustomerId, paymentFormik?.values?.amount, isDonationMode]);

  if (loading) {
    return (
      <Card
        style={{ padding: "16px", background: "#F8FAFC", borderRadius: "12px" }}
      >
        <Typography style={{ color: "#0f2b46", textAlign: "center" }}>
          Loading Apple Pay...
        </Typography>
      </Card>
    );
  }

  if (window.location.protocol !== "https:") {
    return (
      <Card
        style={{ padding: "16px", background: "#F8FAFC", borderRadius: "12px" }}
      >
        <Typography style={{ color: "red", textAlign: "center" }}>
          Apple Pay requires HTTPS
        </Typography>
      </Card>
    );
  }

  if (/Android/.test(navigator.userAgent)) {
    return (
      <Card
        style={{ padding: "16px", background: "#F8FAFC", borderRadius: "12px" }}
      >
        <Typography style={{ color: "red", textAlign: "center" }}>
          Apple Pay not available on Android
        </Typography>
      </Card>
    );
  }

  if (!publicKey) {
    return (
      <Card
        style={{ padding: "16px", background: "#F8FAFC", borderRadius: "12px" }}
      >
        <Typography style={{ color: "red", textAlign: "center" }}>
          Apple Pay not available
        </Typography>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        style={{ padding: "16px", background: "#F8FAFC", borderRadius: "12px" }}
      >
        <Typography style={{ color: "red", textAlign: "center" }}>
          {error}
        </Typography>
      </Card>
    );
  }

  return (
    <Card
      style={{ padding: "16px", background: "#F8FAFC", borderRadius: "12px" }}
    >
      <Typography
        style={{
          color: "#0f2b46",
          fontWeight: "600",
          fontSize: "16px",
          marginBottom: "16px",
        }}
      >
        Apple Pay
      </Typography>

      <div id="applepaybutton"></div>

      {buttonNotFound && (
        <div
          style={{
            marginTop: "12px",
            padding: "12px",
            background: "#FEF3C7",
            border: "1px solid #F59E0B",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <Typography
            style={{
              color: "#92400E",
              fontSize: "14px",
              fontWeight: "500",
              marginBottom: "4px",
            }}
          >
            Apple Pay Configuration Required
          </Typography>
          <Typography style={{ color: "#92400E", fontSize: "12px" }}>
            Please contact support to enable Apple Pay for this domain
          </Typography>
        </div>
      )}

      <Typography
        style={{
          color: "#6B7280",
          fontSize: "12px",
          marginTop: "8px",
          textAlign: "center",
        }}
      >
        Secure payment powered by Apple Pay
      </Typography>
    </Card>
  );
};

export default ApplePayPayment;
